// The items-list inputs of a rapid-association question, ported from its own coffee list view
// (RapidAssociation.ItemsListView#calcItemsListProps) and the shared list it overrode wholesale.
//
// A word is a row: what it says, and how many respondents gave it the affirmative answer. The
// scatter view adds the one column the other two have nothing to put in it -- how long, on average,
// answering took.

import { encodeLabel, formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import {
  getCustomVariables,
  getModelValue,
  getReportVirtual,
  getSurveyVariables,
  getTrait,
  getFeature
} from '@aytm/app/stats/lib/stat-page'
import { isRType as calcIsRType, isTraitType as calcIsTraitType } from '@aytm/app/stats/page/components/lib/utils'

// the palette repeats every 21 rows, and index 0 is reserved for a trait-coloured row
const COLOR_INDEXES = 21

const SCATTER_VIEW_MODE = 'scatter'

const colorIndexOf = position => ((position - 1) % COLOR_INDEXES) + 1

// The trait the report is broken down by, wherever it is defined.
const resolveTrait = traitName => {
  if (null == traitName) return null

  return (
    getTrait() ||
    getSurveyVariables('variables')?.[traitName] ||
    getSurveyVariables('pipingVariables')?.[traitName] ||
    getCustomVariables()?.[traitName] ||
    getReportVirtual()[traitName]
  )
}

const traitValuesOf = trait => {
  if (null == trait) return []

  return (trait.values || trait.answerVariants || []).map(value => ({
    id: (Object.hasOwn(value, 'id') && value.id) || (Object.hasOwn(value, 'position') && `a${value.position}`) || value,
    name:
      (Object.hasOwn(value, 'name') && encodeLabel(value.name.en || value.name)) ||
      (Object.hasOwn(value, 'position') && `A${value.position}`) ||
      value
  }))
}

const calcTraitTable = ({ id, trait, traitName, traitValues, answer }) => ({
  id: `${id}-traits`,
  cols: [
    {
      type: 'title',
      title: encodeLabel(
        (Object.hasOwn(trait, 'title') && trait.title) ||
          (Object.hasOwn(trait, 'id') && traitName.toUpperCase()) ||
          traitName
      )
    },
    // the share names itself where the % marker would sit; the column heads no count slot of its own
    { type: 'progress', title: '', percentLabel: '% of this answer', format: value => formatAmounts(value) }
  ],
  rows: traitValues.map((value, index) => ({
    cells: [
      { title: value.name.en || value.name, colorIndex: (index % COLOR_INDEXES) + 1 },
      { amount: answer[value.id]?.amount || 0, percents: answer[value.id]?.percents || 0 }
    ],
    position: index + 1
  })),
  classNames: ['child']
})

// The column the stored sort names, marked with the direction it is sorted in. An order naming a
// column the list does not offer falls to the last one, the way the coffee list resolved it.
const applySortedColumn = (cols, sortOrder) => {
  const parts = String(sortOrder).split('-')

  const found =
    1 < parts.length
      ? cols.findIndex(col => col.param === parts[0])
      : cols.findIndex(col => (('i' === sortOrder[0] && 'title') || 'progress') === col.type)

  const index = 0 > found ? cols.length - 1 : found
  const direction = 2 === parts.length ? ('asc' === parts[1] && 1) || -1 : ('asc' === sortOrder.slice(1) && 1) || -1

  return cols.map((col, i) => (i === index ? { ...col, sorted: direction } : col))
}

// How long the average respondent took, in the words the coffee list used. A word nobody reached
// has no time to report.
const formatReactionTime = value => (null !== value ? `${value.toFixed(2)} seconds` : '---')

export const calcTableProps = ({
  id,
  type,
  format,
  components,
  answer,
  answerVariants,
  viewMode,
  sortOrder = 'iasc',
  clickable = true,
  handlers = {}
} = {}) => {
  const variants = (answerVariants || []).filter(av => av)

  const isScatter = SCATTER_VIEW_MODE === viewMode

  const traitName = getModelValue('trait')
  const trait = resolveTrait(traitName)
  const traitValues = traitValuesOf(trait)

  const isTraitType = calcIsTraitType({ format, type })
  // the scatter list reads each word whole and never breaks it down by the trait the report is read by
  const isBrokenDown = Boolean(trait) && isTraitType && !isScatter
  const isLoading = 'loading' === answer
  // the bar list follows the page's counting mode; the scatter list always counts respondents, as master's did
  const rType = !isScatter && calcIsRType({ type }) ? getModelValue('rType') : 'cases'

  const percentDecimals = Object.hasOwn(format || {}, 'percentDecimals')
    ? format.percentDecimals
    : getModelValue('percentDecimals')

  const isClickable = undefined !== format?.clickable ? format.clickable : clickable

  const commentsOn =
    (!components || undefined === components.comments || true === components.comments) &&
    'off' !== getFeature('individual_data')?.status?.oe

  const cols = [
    { type: 'title', title: 'Answers', sort: (aRow, bRow, dir) => dir * (aRow.position - bRow.position) },
    {
      type: 'progress',
      title: `${'cases' === rType ? 'Respondents' : 'Responses'} <small>(${formatAmounts(answer, 'cases' === rType ? 'total' : 'respTotal')} total)</small>`,
      percentDecimals,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].percents + aRow.position / 10000 - (bRow.cells[1].percents + bRow.position / 10000))
    }
  ]

  if (isScatter) {
    // last, not between title and figures: the progress label overflows leftward onto what it
    // assumes is the title column's empty stretch, so nothing may stand in between
    cols.push({
      type: 'value',
      title: 'Average time',
      param: 'time',
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[2].value + aRow.position / 10000 - (bRow.cells[2].value + bRow.position / 10000)),
      format: formatReactionTime
    })
  }

  const rows = variants.map(variant => {
    const variantAnswer = answer?.[`a${variant.position}`] || {}

    const title = {
      title: variant.text,
      label: `A${variant.position}`,
      colorIndex: isBrokenDown ? 0 : colorIndexOf(variant.position)
    }

    if (Object.hasOwn(variant, 'image')) title.image = variant.image

    if (commentsOn && Object.hasOwn(variantAnswer, 'comments')) {
      title.comments = variantAnswer.comments
      title.onCommentsClick = handlers.onCommentsClick
    }

    if (isClickable) title.onLabelClick = handlers.onLabelClick

    const cells = [
      title,
      // counting responses, a word reads its share of the responses; one that carries none reads 0, as on master
      'cases' === rType
        ? { amount: variantAnswer.amount || 0, percents: variantAnswer.percents || 0, loading: isLoading }
        : undefined === variantAnswer.respPercents
          ? { amount: 0, percents: 0, loading: isLoading }
          : { amount: variantAnswer.amount || 0, percents: variantAnswer.respPercents || 0, loading: isLoading }
    ]

    // the reaction time rides the thumbs-up envelope, not the variant root -- the legacy scatter
    // list read its whole row from `answer.aN.y`
    if (isScatter) cells.push({ value: variantAnswer.y?.rt || null, loading: isLoading })

    const row = { position: variant.position, cells }

    // a trait breakdown is a table of its own the row expands into
    if (isBrokenDown && undefined !== variantAnswer[traitName]) {
      row.table = calcTraitTable({ id, trait, traitName, traitValues, answer: variantAnswer[traitName] })
    }

    return row
  })

  // No sort callback: this list reads the order the report holds and marks the column it names,
  // but has never written one back -- its coffee view replaced the shared list's props wholesale
  // and dropped the handler with them.
  return { id, cols: applySortedColumn(cols, sortOrder), rows }
}
